import { watch, onMounted, onUnmounted, type Ref } from 'vue';
import { currentUserHeading } from '@deliveryteam-spa-orders-vue/refs/UserCurrentLocation';
import { courierMode } from '@deliveryteam-spa-orders-vue/refs/CourierProfile';
import { loadLeafletCDN } from '@/leaflet/loadLeafletCDN';
import { myOrdersList, type ordersType } from '@deliveryteam-spa-orders-vue/refs/RecentOrders';
import { authState } from "@/ui-plus/useAuth";
import { DELIVERYTEAM_ORDER_STATUS_OBJ } from '@lib/types/deliveryteamOrderStatus';
import { computed } from 'vue';
import { calculateDistance } from '@lib/utils/calculateDistance';

/** Single combined route on the map (not per-order legs). */
const COMBINED_ROUTE_COLOR = '#2563eb';

type RouteWaypoint = {
    orderId: string;
    kind: 'pickup' | 'delivery';
    lat: number;
    lng: number;
};

/** Courier is past pickup: delivery-only in the waypoint pool. */
function isPostPickupStatus(orderStatus: string): boolean {
    return (
        orderStatus === DELIVERYTEAM_ORDER_STATUS_OBJ.PICKED_UP
        || orderStatus === DELIVERYTEAM_ORDER_STATUS_OBJ.GOING_TO_DELIVERY
        || orderStatus === DELIVERYTEAM_ORDER_STATUS_OBJ.WAITING_AT_DELIVERY
    );
}

function waypointsFromOrder(order: ordersType): RouteWaypoint[] {
    const id = order.deliveryteamOrderId;
    const postPickup = isPostPickupStatus(order.orderStatus);
    const pickupLat = order.pickupAddressJSON?.lat;
    const pickupLng = order.pickupAddressJSON?.lng;
    const deliveryLat = order.deliveryAddressJSON?.lat;
    const deliveryLng = order.deliveryAddressJSON?.lng;

    const out: RouteWaypoint[] = [];
    if (!postPickup && pickupLat != null && pickupLng != null) {
        out.push({ orderId: id, kind: 'pickup', lat: pickupLat, lng: pickupLng });
    }
    if (deliveryLat != null && deliveryLng != null) {
        out.push({ orderId: id, kind: 'delivery', lat: deliveryLat, lng: deliveryLng });
    }
    return out;
}

/** Delivery is eligible only when that order’s pickup is not still unvisited (or was never in the pool). */
function isWaypointEligible(w: RouteWaypoint, remaining: RouteWaypoint[]): boolean {
    if (w.kind === 'pickup') return true;
    const pickupStillInRemaining = remaining.some(
        (r) => r.orderId === w.orderId && r.kind === 'pickup',
    );
    return !pickupStillInRemaining;
}

function distToPoint(
    lat: number,
    lng: number,
    w: RouteWaypoint,
): number {
    return calculateDistance({ lat, lng }, { lat: w.lat, lng: w.lng }, 'km');
}

/** Deterministic tie-break when two stops are equally far. */
function compareWaypoints(a: RouteWaypoint, b: RouteWaypoint): number {
    const c = a.orderId.localeCompare(b.orderId);
    if (c !== 0) return c;
    if (a.kind === b.kind) return 0;
    return a.kind === 'pickup' ? -1 : 1;
}

function pickNearestEligible(
    fromLat: number,
    fromLng: number,
    remaining: RouteWaypoint[],
): RouteWaypoint | null {
    const eligible = remaining.filter((w) => isWaypointEligible(w, remaining));
    if (eligible.length === 0) return null;
    let best = eligible[0];
    let bestD = distToPoint(fromLat, fromLng, best);
    for (let i = 1; i < eligible.length; i++) {
        const w = eligible[i];
        const d = distToPoint(fromLat, fromLng, w);
        if (d < bestD || (d === bestD && compareWaypoints(w, best) < 0)) {
            best = w;
            bestD = d;
        }
    }
    return best;
}

function centroidOfWaypoints(ws: RouteWaypoint[]): { lat: number; lng: number } {
    let slat = 0;
    let slng = 0;
    for (const w of ws) {
        slat += w.lat;
        slng += w.lng;
    }
    const n = ws.length;
    return { lat: slat / n, lng: slng / n };
}

/**
 * Nearest-neighbor chain from user when GPS exists; otherwise seed from the stop closest to the
 * centroid of all stops, then continue NN (polyline uses real stops only — no fake centroid vertex).
 */
function orderedStopsConstrainedNN(
    userLat: number | null,
    userLng: number | null,
    waypoints: RouteWaypoint[],
): RouteWaypoint[] {
    if (waypoints.length === 0) return [];

    const remaining = [...waypoints];
    const ordered: RouteWaypoint[] = [];

    let fromLat: number;
    let fromLng: number;

    if (userLat != null && userLng != null) {
        fromLat = userLat;
        fromLng = userLng;
    } else {
        const c = centroidOfWaypoints(remaining);
        const first = pickNearestEligible(c.lat, c.lng, remaining);
        if (!first) return [];
        remaining.splice(remaining.indexOf(first), 1);
        ordered.push(first);
        fromLat = first.lat;
        fromLng = first.lng;
    }

    while (remaining.length > 0) {
        const next = pickNearestEligible(fromLat, fromLng, remaining);
        if (!next) break;
        remaining.splice(remaining.indexOf(next), 1);
        ordered.push(next);
        fromLat = next.lat;
        fromLng = next.lng;
    }

    return ordered;
}

/** Collapse consecutive duplicate coordinates (same lat/lng). */
function toLeafletPathLatLngs(
    userLat: number | null,
    userLng: number | null,
    ordered: RouteWaypoint[],
): [number, number][] {
    const seq: { lat: number; lng: number }[] = [];
    if (userLat != null && userLng != null) {
        seq.push({ lat: userLat, lng: userLng });
    }
    for (const w of ordered) {
        seq.push({ lat: w.lat, lng: w.lng });
    }
    const out: [number, number][] = [];
    for (const p of seq) {
        const prev = out[out.length - 1];
        if (prev && prev[0] === p.lat && prev[1] === p.lng) continue;
        out.push([p.lat, p.lng]);
    }
    return out;
}

function buildOrderedRoutePath(
    userLat: number | null,
    userLng: number | null,
    orders: ordersType[],
): [number, number][] {
    const allWaypoints = orders.flatMap(waypointsFromOrder);
    const orderedStops = orderedStopsConstrainedNN(
        userLat,
        userLng,
        allWaypoints,
    );
    return toLeafletPathLatLngs(
        userLat,
        userLng,
        orderedStops,
    );
}

// TODO: use real address instead of lat/lng for waypoints and destination
// with getAddress function from @lib/utils/checkGeocodeDistance
function buildGoogleDirectionsEmbedUrl(path: [number, number][]): string {
    if (path.length === 1) {
        // first location, user's current location only
        const q = `${path[0][0]},${path[0][1]}`;
        return `https://www.google.com/maps/embed/v1/place?`
            + `q=${encodeURIComponent(q)}`
            + `&key=${import.meta.env.PUBLIC_GMAPS_EMBED_API_KEY}`;
    }
    if (path.length < 2) return '';
    // first location, user's current location
    const origin = `${path[0][0]},${path[0][1]}`;
    // last location, delivery location
    const destination = `${path[path.length - 1][0]},${path[path.length - 1][1]}`;
    // waypoints, pickup locations 0 or many
    const waypointPoints = path
        .slice(1, -1)
        .slice(0, 20)
        .map(([lat, lng]) => `${lat},${lng}`);
    return `https://www.google.com/maps/embed/v1/directions?`
        + `origin=${encodeURIComponent(origin)}`
        + `&destination=${encodeURIComponent(destination)}`
        + (waypointPoints.length > 0 ? `&waypoints=${encodeURIComponent(waypointPoints.join('|'))}` : '')
        + `&mode=` + courierMode.value.toLowerCase()
        + `&key=${import.meta.env.PUBLIC_GMAPS_EMBED_API_KEY}`;
}

function buildGoogleDirectionsLinkUrl(path: [number, number][]): string {
    if (path.length === 1) {
        const q = `${path[0][0]},${path[0][1]}`;
        return `https://www.google.com/maps/search/?api=1&`
            + `query=${encodeURIComponent(q)}`
    }
    if (path.length < 2) return '';
    const origin = `${path[0][0]},${path[0][1]}`;
    const destination = `${path[path.length - 1][0]},${path[path.length - 1][1]}`;
    const waypointPoints = path
        .slice(1, -1)
        .slice(0, 20)
        .map(([lat, lng]) => `${lat},${lng}`);
    return `https://www.google.com/maps/dir/?api=1`
        + `&destination=${encodeURIComponent(destination)}`
        + `&origin=${encodeURIComponent(origin)}`
        + `&travelmode=${encodeURIComponent(courierMode.value.toLowerCase())}`
        + (waypointPoints.length > 0 ? `&waypoints=${encodeURIComponent(waypointPoints.join('|'))}` : '');
}

export function getClosestOrderIdByRouteSorting(
    orders: ordersType[],
    userLat: number | null,
    userLng: number | null,
): string | null {
    const waypoints = orders.flatMap(waypointsFromOrder);
    const orderedStops = orderedStopsConstrainedNN(userLat, userLng, waypoints);
    const firstStop = orderedStops[0];
    if (!firstStop) return null;
    return firstStop.orderId;
}

/** Courier's active orders (includes scheduled — listed in UI but not drawn on the map). */
export const courierOrders = computed(() =>
    myOrdersList.value
        // user filter not required anymore
        .filter(order => order.courierId === authState.value?.accessTokenPayload?.id)
        .filter((o) =>
            o.orderStatus !== DELIVERYTEAM_ORDER_STATUS_OBJ.DELIVERED &&
            o.orderStatus !== DELIVERYTEAM_ORDER_STATUS_OBJ.UNDELIVERABLE_COMPLETED &&
            o.orderStatus !== DELIVERYTEAM_ORDER_STATUS_OBJ.CANCELLED &&
            o.orderStatus !== DELIVERYTEAM_ORDER_STATUS_OBJ.COMPLETED
        )
);

/** Markers, combined route, and directions URLs — excludes scheduled orders. */
export const courierOrdersOnMap = computed(() =>
    courierOrders.value.filter(
        (o) => o.orderStatus !== DELIVERYTEAM_ORDER_STATUS_OBJ.SCHEDULED,
    )// sort oldest first
        .sort((a, b) => a.order_placed_at_ts - b.order_placed_at_ts)
);

const ROUTE_COLORS = [
    '#2563eb', // blue-600
    '#020617', // slate-950, works on light bg
    '#c026d3', // fuchsia-600
    '#ea580c', // orange-600
    '#0891b2', // cyan-600
    '#ca8a04', // yellow-600
    '#d97706', // amber-600
    '#f43f5e', // rose-600
    '#e11d48', // red-700
    '#10b981', // emerald-500
    '#0ea5e9', // sky-500
    '#8b5cf6', // violet-500
    '#a3e635', // lime-400
    '#fbbf24', // yellow-400
    '#f472b6', // pink-400
    '#6ee7b7', // teal-300
    '#16a34a', // green-600
    '#7dd3fc', // blue-300
    '#facc15', // yellow-400
    '#f9fafb', // gray-50, works on dark bg
];

/** Initial bearing from (lat1,lng1) toward (lat2,lng2), degrees clockwise from north. */
function bearingDegrees(lat1: number, lng1: number, lat2: number, lng2: number): number {
    const φ1 = (lat1 * Math.PI) / 180;
    const φ2 = (lat2 * Math.PI) / 180;
    const Δλ = ((lng2 - lng1) * Math.PI) / 180;
    const y = Math.sin(Δλ) * Math.cos(φ2);
    const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
    return (Math.atan2(y, x) * 180) / Math.PI;
}

const ARROW_EVERY_KM = 0.5;
// const MAX_ARROWS_PER_ROUTE = 48;

function cumulativeDistancesKm(path: [number, number][]): number[] {
    const cum: number[] = [0];
    for (let i = 0; i < path.length - 1; i++) {
        const [lat1, lng1] = path[i];
        const [lat2, lng2] = path[i + 1];
        const seg = calculateDistance({ lat: lat1, lng: lng1 }, { lat: lat2, lng: lng2 }, 'km');
        cum.push(cum[i] + seg);
    }
    return cum;
}

/** Point at distance `km` along the polyline + segment index for bearing. */
function pointAndSegmentAtKm(
    path: [number, number][],
    cum: number[],
    km: number,
): { lat: number; lng: number; seg: number } {
    const last = path.length - 1;
    let i = 0;
    while (i < last && cum[i + 1] < km) i++;
    if (i >= last) {
        const [lat, lng] = path[last];
        return { lat, lng, seg: last - 1 };
    }
    const segStart = cum[i];
    const segEnd = cum[i + 1];
    const t = segEnd > segStart ? (km - segStart) / (segEnd - segStart) : 0;
    const [lat1, lng1] = path[i];
    const [lat2, lng2] = path[i + 1];
    return {
        lat: lat1 + t * (lat2 - lat1),
        lng: lng1 + t * (lng2 - lng1),
        seg: i,
    };
}

function addArrowsAlongPath(
    map: L.Map,
    path: [number, number][],
    color: string,
    into: (L.Marker | L.Polyline)[],
) {
    const last = path.length - 1;
    if (path.length < 2) return;

    const cum = cumulativeDistancesKm(path);
    const totalKm = cum[last];
    if (totalKm <= 0) return;

    const n = Math.min(
        Math.max(1, Math.floor(totalKm / ARROW_EVERY_KM)),
        30 // MAX_ARROWS_PER_ROUTE,
    );
    // const n = Math.max(1, Math.floor(totalKm / ARROW_EVERY_KM));
    const tailKm = Math.min(0.12, totalKm * 0.15);
    const capKm = Math.min(
        totalKm - 1e-6,
        Math.max(ARROW_EVERY_KM * 0.2, totalKm - tailKm),
    );

    for (let k = 1; k <= n; k++) {
        const kmAlong = Math.min(k * ARROW_EVERY_KM, capKm);
        const { lat, lng, seg } = pointAndSegmentAtKm(path, cum, kmAlong);
        const bearing = bearingDegrees(
            path[seg][0],
            path[seg][1],
            path[seg + 1][0],
            path[seg + 1][1],
        );
        into.push(
            window.L.marker([lat, lng], {
                interactive: false,
                keyboard: false,
                icon: window.L.divIcon({
                    className: '',
                    html: `<div style="width:18px;height:18px;display:flex;align-items:center;justify-content:center;pointer-events:none;">
  <svg width="12" height="12" viewBox="0 0 12 12" style="transform:rotate(${bearing}deg);transform-origin:center center;">
    <path d="M6 1 L11 10.5 L6.75 8.25 L6 7.6 L5.25 8.25 L1 10.5 Z" fill="${color}" stroke="#ffffff" stroke-width="1.1" stroke-linejoin="round"/>
  </svg>
</div>`,
                    iconSize: [18, 18],
                    iconAnchor: [9, 9],
                }),
            }).addTo(map)
        );
    }
}

export function useMyOrdersMap(
    userLat: Ref<number | null>,
    userLng: Ref<number | null>,
) {
    let map: L.Map | null = null;
    let layers: (L.Marker | L.Polyline)[] = [];
    const googleDirectionsEmbedUrl = computed(() =>
        buildGoogleDirectionsEmbedUrl(
            buildOrderedRoutePath(userLat.value, userLng.value, courierOrdersOnMap.value),
        ),
    );
    const googleDirectionsLinkUrl = computed(() =>
        buildGoogleDirectionsLinkUrl(
            buildOrderedRoutePath(userLat.value, userLng.value, courierOrdersOnMap.value),
        ),
    );

    const clearLayers = () => {
        layers.forEach((l) => map?.removeLayer(l));
        layers.length = 0;
    };

    const redraw = () => {
        if (!map) return;
        clearLayers();

        const bounds = window.L.latLngBounds([]);

        if (userLat.value != null && userLng.value != null) {
            const h = currentUserHeading.value;
            const hasHeading = h != null && Number.isFinite(h);
            const userMarkerIcon = hasHeading
                ? window.L.divIcon({
                    html: `<div style="width:26px;height:26px;display:flex;align-items:center;justify-content:center;pointer-events:none;">
  <svg width="24" height="24" viewBox="0 0 12 12" style="transform:rotate(${h}deg);transform-origin:center center;">
    <path d="M6 1 L11 10.5 L6.75 8.25 L6 7.6 L5.25 8.25 L1 10.5 Z" fill="#000000" stroke="#ffffff" stroke-width="1.2" stroke-linejoin="round"/>
  </svg>
</div>`,
                    className: '',
                    iconSize: [26, 26],
                    iconAnchor: [13, 13],
                })
                : window.L.divIcon({
                    html: `<div style="background-color:#000000;width:14px;height:14px;border-radius:50%;border:2px solid #ffffff;box-shadow:0 0 2px rgba(0,0,0,0.4);pointer-events:none;"></div>`,
                    className: '',
                    iconSize: [14, 14],
                    iconAnchor: [7, 7],
                });
            layers.push(
                window.L.marker([userLat.value, userLng.value], {
                    icon: userMarkerIcon,
                }).addTo(map).bindPopup('Your location')
            );
            bounds.extend([userLat.value, userLng.value]);
        }

        courierOrdersOnMap.value.forEach((order, index) => {
            const color = ROUTE_COLORS[index % ROUTE_COLORS.length];
            const pickupLat = order.pickupAddressJSON?.lat;
            const pickupLng = order.pickupAddressJSON?.lng;
            const deliveryLat = order.deliveryAddressJSON?.lat;
            const deliveryLng = order.deliveryAddressJSON?.lng;

            if (pickupLat != null && pickupLng != null) {
                layers.push(
                    window.L.marker([pickupLat, pickupLng], {
                        icon: window.L.divIcon({
                            html: `<div style="background-color: ${color}; width: 12px; height: 12px; border-radius: 50%; border: 2px solid white; box-shadow: 0 0 2px rgba(0,0,0,0.4);"></div>`,
                            className: '',
                            iconSize: [12, 12],
                            iconAnchor: [6, 6],
                        }),
                    }).addTo(map!).bindPopup(`Pickup · #${order.shortOtp}`)
                );
                // if (
                //     userLat.value != null && userLng.value != null
                //     && calculateDistance({ lat: pickupLat, lng: pickupLng }, { lat: userLat.value, lng: userLng.value }) < 2
                // ) {
                //     bounds.extend([pickupLat, pickupLng]);
                // }
            }

            if (deliveryLat != null && deliveryLng != null) {
                layers.push(
                    window.L.marker([deliveryLat, deliveryLng], {
                        icon: window.L.divIcon({
                            html: `<div style="background-color: ${color}; width: 16px; height: 16px; border: 2px solid #0a0a0a;"></div>`,
                            className: '',
                            iconSize: [16, 16],
                            iconAnchor: [8, 8],
                        }),
                    }).addTo(map!).bindPopup(`Delivery · #${order.shortOtp}`)
                );
                // if (
                //     userLat.value != null && userLng.value != null
                //     && calculateDistance({ lat: deliveryLat, lng: deliveryLng }, { lat: userLat.value, lng: userLng.value }) < 2
                // ) {
                //     bounds.extend([deliveryLat, deliveryLng]);
                // }
            }
        });

        const routePath = buildOrderedRoutePath(
            userLat.value,
            userLng.value,
            courierOrdersOnMap.value,
        );

        if (routePath.length >= 2) {
            layers.push(
                window.L.polyline(routePath, {
                    color: COMBINED_ROUTE_COLOR,
                    weight: 3,
                    opacity: 0.85,
                    dashArray: '8, 6',
                }).addTo(map!)
            );
            addArrowsAlongPath(map!, routePath, COMBINED_ROUTE_COLOR, layers);

            // only add next point in routePath to bounds
            if (routePath.length > 1) {
                bounds.extend([routePath[1][0], routePath[1][1]]);
            }
        }

        if (bounds.isValid()) {
            setTimeout(() => {
                map?.fitBounds(bounds, {
                    padding: [28, 28],
                    maxZoom: (map.getZoom() ?? 18),
                    animate: true,
                });
            }, 50);
        }
    };

    const initMap = async (mapId: string) => {
        const loaded = await loadLeafletCDN(true);
        if (!loaded) return;

        let center = { lat: 40.7128, lng: -74.006 };
        const first = courierOrdersOnMap.value[0];
        const ulat = first?.pickupAddressJSON?.lat;
        const ulng = first?.pickupAddressJSON?.lng;
        if (ulat != null && ulng != null) {
            center = { lat: ulat, lng: ulng };
        } else if (userLat.value != null && userLng.value != null) {
            center = { lat: userLat.value, lng: userLng.value };
        }

        // leaflet-gesture-handling (loaded after CDN Leaflet) — two-finger pan on touch, like Google Maps
        map = window.L.map(mapId, {
            // @ts-ignore
            gestureHandling: true,
        }).setView(
            [center.lat, center.lng],
            18,
        );

        map.attributionControl.setPrefix(false);
        map.addLayer(
            window.L.tileLayer('https://tile.openstreetmap.org/{z}/{x}/{y}.png', {
                maxZoom: 19,
                attribution:
                    '<a target="_blank" rel="noopener noreferrer" href="https://osm.org/copyright">© OpenStreetMap</a>',
                // @ts-ignore
                referrerPolicy: 'strict-origin-when-cross-origin'
            })
        );

        redraw();
    };

    const onResize = () => {
        map?.invalidateSize();
        redraw();
    };

    watch(courierOrders, redraw, { deep: true });
    watch([userLat, userLng], redraw);
    watch(currentUserHeading, redraw);

    onMounted(() => window.addEventListener('resize', onResize));
    onUnmounted(() => {
        window.removeEventListener('resize', onResize);
        if (map) {
            map.remove();
            map = null;
        }
    });

    const getMapCenter = () => {
        if (!map) return null;
        return map.getCenter();
    };

    return { initMap, redraw, getMapCenter, googleDirectionsEmbedUrl, googleDirectionsLinkUrl };
}
